import { type FC, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import type { RecapResponse } from '@eluve/api-contract';
import { Grid, Icon, Stack, Text } from '@eluve/components';

const compactDate = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(2, 4)}`;
};

export const PreVisitRecap: FC<{
  tenantId: string;
  appointmentId: string;
  patientId: string | null;
}> = ({ tenantId, appointmentId, patientId }) => {
  const tsRestClient = useApiClient();
  const [recap, setRecap] = useState<RecapResponse | null>(null);

  useEffect(() => {
    setRecap(null);
    if (!patientId) return;

    let alive = true;
    void tsRestClient.carePlans
      .getRecap({ params: { tenantId }, query: { appointmentId } })
      .then((r) => {
        if (alive && r.status === 200) setRecap(r.body);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [tsRestClient, tenantId, appointmentId, patientId]);

  if (
    !recap ||
    (recap.goals.length === 0 &&
      recap.lastVisitActionItems.length === 0 &&
      recap.measuresDue.length === 0)
  ) {
    return null;
  }

  const heading = [
    recap.visitNumber !== null ? `Visit ${recap.visitNumber}` : null,
    recap.phase
      ? `Phase ${recap.phase.current}${
          recap.phase.name
            ? ` · ${recap.phase.name.replace(/^Phase\s+\d+\s*[—–-]\s*/i, '')}`
            : ''
        }`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="w-full rounded-lg border border-border-secondary bg-bg-primary px-4 py-3.5">
      <Stack direction="vertical" gap={0}>
        <Text size="md" weight="semibold">
          Pre-visit recap
        </Text>
        {heading && (
          <Text size="xs" weight="medium" color="tertiary">
            {heading}
          </Text>
        )}
      </Stack>

      <Grid columns={1} className="mt-2 sm:grid-cols-3">
        <RecapColumn title="Goals" className="sm:pr-4">
          {recap.goals.length === 0 ? (
            <Empty>No goals on the plan yet.</Empty>
          ) : (
            recap.goals.map((g) => (
              <Stack key={g.id} direction="vertical" gap={0}>
                <Text size="xs">{g.text}</Text>
                {g.targetDate && (
                  <Text size="xs" color="tertiary">
                    Target {compactDate(g.targetDate)}
                  </Text>
                )}
              </Stack>
            ))
          )}
        </RecapColumn>

        <RecapColumn title="Done last visit" className="sm:border-l sm:px-4">
          {recap.lastVisitActionItems.length === 0 ? (
            <Empty>No exercises from the last visit.</Empty>
          ) : (
            <>
              {recap.lastVisitActionItems.map((a) => (
                <Stack key={a.id} direction="horizontal" align="start" gap={2}>
                  <Icon
                    name="Check"
                    size="xs"
                    className="mt-0.5 shrink-0 text-text-tertiary"
                  />
                  <Text size="xs">{a.text}</Text>
                </Stack>
              ))}
              <Text size="xs" color="tertiary">
                {recap.lastVisitActionItems.length} completed
              </Text>
            </>
          )}
        </RecapColumn>

        <RecapColumn title="Address this visit" className="sm:border-l sm:pl-4">
          {recap.measuresDue.length === 0 ? (
            <Empty>Nothing flagged as due.</Empty>
          ) : (
            recap.measuresDue.map((m) => (
              <Text key={m.key} size="xs">
                {m.label}{' '}
                {m.lastValue !== null && (
                  <span className="text-text-tertiary">
                    · last {m.lastValue}
                    {m.unit ? ` ${m.unit}` : ''}
                    {m.lastDate ? ` (${compactDate(m.lastDate)})` : ''}
                  </span>
                )}
              </Text>
            ))
          )}
        </RecapColumn>
      </Grid>
    </section>
  );
};

const RecapColumn: FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => (
  <Stack
    direction="vertical"
    gap={2}
    className={`border-border-secondary py-4 ${className ?? ''}`}
  >
    <Text
      size="xs"
      weight="bold"
      color="tertiary"
      className="tracking-wide uppercase"
    >
      {title}
    </Text>
    {children}
  </Stack>
);

const Empty: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="xs" color="tertiary">
    {children}
  </Text>
);
